import baseTheme from '../common/base-theme';
import type { ResumeTheme } from '../common/base-theme';
import { getCalcSize } from '../common/calc-size';
import { SanJuanCustomizations } from '../templates-info';

const sanJuanTheme = (customizations: SanJuanCustomizations): ResumeTheme => {
  const calcSize = getCalcSize(customizations.baseSize);
  const theme = {
    ...baseTheme,
    baseSize: customizations.baseSize,
    print: {
      ...baseTheme.print,
      backgroundColor: customizations.background,
      paddingX: calcSize(7.5, 110, 130),
      paddingY: calcSize(3.75, 40, 70),
    },
    web: { ...baseTheme.web, backgroundColor: customizations.background },
    // Set after based on customizations
    fonts: {},
    // Set after based on customizations
    fontSizes: [],
    colors: {
      primaryText: customizations.text1,
      secondaryText: customizations.text2,
      tertiaryText: customizations.text3,
      accent1: customizations.accent1,
      accent2: customizations.accent2,
      skillFill: '#f1f5f7',
      background: customizations.background,
    },
    // Set after based on customizations
    fontWeights: {},
    lineHeights: {
      body: 1.5,
      heading: 1,
    },
    letterSpacings: {
      body: 'normal',
      caps: '1.2px',
    },
    text: {
      // Base text styles. Inherited by everything else.
      heading: {
        fontFamily: 'heading',
        lineHeight: 'heading',
        fontWeight: 'heading',
        textTransform: 'uppercase',
        color: 'primaryText',
      },
      body: {
        fontFamily: 'body',
        lineHeight: 'body',
        fontWeight: 'body',
        color: 'primaryText',
        fontSize: 1,
      },
      // Resume text elements
      resumeHeading: {
        variant: 'text.heading',
        fontSize: 3,
        letterSpacing: '1.3px',
        marginBottom: calcSize(1.5),
        paddingTop: [86, 0],
      },
      sectionHeading: {
        variant: 'text.heading',
        fontSize: 0,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        color: 'tertiaryText',
        marginBottom: calcSize(2),
      },
      sectionItemHeading: {
        variant: 'text.heading',
        fontSize: 2,
        lineHeight: '24px',
        letterSpacing: '0.07em',
        marginBottom: calcSize(0.5),
      },
      sectionItemSubHeading: {
        color: 'secondaryText',
        fontSize: 1,
        lineHeight: '24px',
        marginBottom: calcSize(0.25),
        fontFamily: 'body',
      },
      paragraph: {
        variant: 'text.body',
        mb: calcSize(0.5),
      },
      listItem: {
        variant: 'text.body',
        mb: calcSize(0.25),
      },
      summaryListItem: {
        variant: 'text.listItem',
        marginLeft: 30,
        paddingLeft: 0,
      },
    },
    // Containers used to add margin bottom
    headerContainer: {
      marginBottom: calcSize(3),
    },
    sectionContainer: {
      marginBottom: calcSize(3.75),
    },
    detailSectionItemContainer: {
      marginBottom: calcSize(2),
    },
    blockListSectionItemContainer: {
      marginBottom: calcSize(1.25),
    },
    summaryList: {
      padding: 0,
    },
  };

  if (customizations.fontStyle === 'technical') {
    theme.fonts = {
      body: '"IBM Plex Mono", monospace',
      heading: '"IBM Plex Mono", monospace',
    };
    theme.fontWeights = {
      body: 400,
      heading: 500,
      bold: 500,
    };
  } else if (customizations.fontStyle === 'classic') {
    theme.fonts = {
      body: 'lora, sans-serif',
      heading: 'lora, sans-serif',
    };
    theme.fontWeights = {
      body: 400,
      heading: 700,
      bold: 700,
    };
  } else {
    theme.fonts = {
      body: 'LftEtica, lft-etica, -apple-system, sans-serif',
      heading: 'LftEtica, lft-etica, -apple-system, sans-serif',
    };
    theme.fontWeights = {
      body: 300,
      heading: 600,
      bold: 600,
    };
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */
  if (customizations.fontSize === 'tiny') {
    // @ts-ignore: The theme type doesn't support this
    theme.fontSizes = [11, 13, 15, 24];
  } else if (customizations.fontSize === 'small') {
    // @ts-ignore: The theme type doesn't support this
    theme.fontSizes = [12, 14, 16, 26];
  } else if (customizations.fontSize === 'large') {
    // @ts-ignore: The theme type doesn't support this
    theme.fontSizes = [14, 18, 22, 32];
  } else {
    // Used for fontSize normal and unset values
    // @ts-ignore: The theme type doesn't support this
    theme.fontSizes = [12, 16, 18, 28];
  }

  return theme;
};

export default sanJuanTheme;
